import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Bell, BellOff, Send, AlertCircle, CheckCircle2, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { usePushNotifications } from '@/hooks/usePushNotifications';

export default function PushNotificationSettings({ open, onClose, user }) {
  const {
    isSupported,
    isSubscribed,
    permissionStatus,
    loading,
    subscribe,
    unsubscribe,
    sendTestNotification
  } = usePushNotifications(user);
  
  const [testingSent, setTestingSent] = useState(false);

  const handleToggle = async (enabled) => {
    try {
      if (enabled) {
        await subscribe();
        toast.success('Push notifications enabled');
      } else {
        await unsubscribe();
        toast.success('Push notifications disabled');
      }
    } catch (error) {
      if (error.message === 'Notification permission denied') {
        toast.error('Please allow notifications in your browser settings');
      } else {
        toast.error('Failed to update notification settings');
      }
    }
  };

  const handleTestNotification = async () => {
    try {
      await sendTestNotification();
      setTestingSent(true);
      toast.success('Test notification sent');
      setTimeout(() => setTestingSent(false), 3000);
    } catch (error) {
      toast.error('Failed to send test notification');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px] max-h-[85vh]" data-testid="push-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#2563eb]" />
            Push Notification Settings
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {!isSupported ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Not Supported</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Your browser doesn&apos;t support push notifications. Try using Chrome, Firefox, or Edge.
                  </p>
                </div>
              </div>
            </div>
          ) : permissionStatus === 'denied' ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <BellOff className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Notifications Blocked</p>
                  <p className="text-sm text-red-700 mt-1">
                    Notifications are blocked in your browser. Please update your browser settings to allow notifications from this site.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  {isSubscribed ? (
                    <Bell className="w-6 h-6 text-green-600" />
                  ) : (
                    <BellOff className="w-6 h-6 text-slate-400" />
                  )}
                  <div>
                    <Label className="text-base font-medium">Push Notifications</Label>
                    <p className="text-sm text-slate-500">
                      {isSubscribed ? 'You will receive alerts' : 'Enable to receive alerts'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={isSubscribed}
                  onCheckedChange={handleToggle}
                  disabled={loading}
                  data-testid="push-toggle"
                />
              </div>

              {/* Info Section */}
              <div className="space-y-3">
                <p className="text-sm">
                  When enabled, you&apos;ll receive instant notifications for:
                </p>
                <ul className="text-sm text-slate-600 space-y-2 ml-4">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    New appointment assignments
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    Assignment changes to your appointments
                  </li>
                </ul>
              </div>

              {/* Test Notification */}
              {isSubscribed && (
                <div className="pt-4 border-t border-slate-200">
                  <Button
                    variant="outline"
                    onClick={handleTestNotification}
                    disabled={loading || testingSent}
                    className="w-full"
                    data-testid="test-notification-btn"
                  >
                    {testingSent ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />
                        Test Sent!
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Test Notification
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* PWA Install Hint */}
              <div className="pt-4 border-t border-slate-200">
                <div className="flex items-start gap-3 text-slate-500">
                  <Smartphone className="w-5 h-5 mt-0.5" />
                  <p className="text-sm">
                  For the best experience, install this app to your device. Look for &quot;Add to Home Screen&quot; in your browser menu.
                </p>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
